import { useTranslation } from "react-i18next"

import { ArrowDown, ArrowUp } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type MobileSortControlsProps = {
  ariaLabel: string
  direction: "asc" | "desc" | null
  options: Array<{ id: string; label: string }>
  sortKey: string | null
  onSortChange: (key: string, direction: "asc" | "desc") => void
}

export function MobileSortControls({
  ariaLabel,
  direction,
  options,
  sortKey,
  onSortChange,
}: MobileSortControlsProps) {
  const { t } = useTranslation()
  const resolvedDirection = direction ?? "asc"
  const directionLabel =
    resolvedDirection === "asc"
      ? t("adminDashboard.sort.ascending", "Ascending")
      : t("adminDashboard.sort.descending", "Descending")

  return (
    <div className="bg-muted/20 flex items-end gap-2 rounded-lg border p-3 lg:hidden">
      <div className="min-w-0 flex-1">
        <span className="text-muted-foreground mb-1.5 block text-xs font-semibold tracking-wider uppercase">
          {t("adminDashboard.sort.label", "Sort by")}
        </span>
        <Select value={sortKey ?? undefined} onValueChange={(key) => onSortChange(key, "asc")}>
          <SelectTrigger aria-label={ariaLabel} className="min-h-11 w-full rounded-md">
            <SelectValue placeholder={t("adminDashboard.sort.placeholder", "Choose field")} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        className="size-11 shrink-0"
        disabled={!sortKey}
        size="icon"
        type="button"
        variant="outline"
        aria-label={t("adminDashboard.sort.directionLabel", {
          defaultValue: "Sort direction: {{direction}}",
          direction: directionLabel,
        })}
        onClick={() => {
          if (sortKey) {
            onSortChange(sortKey, resolvedDirection === "asc" ? "desc" : "asc")
          }
        }}
      >
        {resolvedDirection === "asc" ? (
          <ArrowUp aria-hidden="true" className="size-4" />
        ) : (
          <ArrowDown aria-hidden="true" className="size-4" />
        )}
      </Button>
    </div>
  )
}
